/**
 * Review Queue — Edit Test Case flyout (matches the provided Edit Test Case screen).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ReactComponent as DropdownIcon } from "../../asserts/logo/dropdown.svg";
import { expectedBehaviorFieldLabels } from "../../utils/testCaseExpectedLabels";
import { unwrapResponseText } from "../../utils/unwrapResponseText";

const FONT = "Lato, sans-serif";

const TEST_TYPES = ["nominal", "edge", "adversarial", "failure_mode"];
const TEST_TYPE_LABELS = {
  nominal: "Functional",
  functional: "Functional",
  edge: "Edge",
  adversarial: "Adversarial",
  failure_mode: "Failure mode",
};

const STATUS_LABEL = {
  not_reviewed: "Not Validated",
  verified: "Verified",
  flagged: "Flagged",
  deactivated: "Deactivated",
};

const STATUS_SX = {
  not_reviewed: { bgcolor: "#FEF3C7", color: "#B45309" },
  verified: { bgcolor: "#DCFCE7", color: "#15803D" },
  flagged: { bgcolor: "#FFEDD5", color: "#C2410C" },
  deactivated: { bgcolor: "#F5F5F5", color: "#707070" },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    bgcolor: "#fff",
    fontFamily: FONT,
    fontSize: 14,
    color: "#555555",
    "& fieldset": { borderColor: "#D0D5DD" },
    "&:hover fieldset": { borderColor: "#98A2B3" },
    "&.Mui-focused fieldset": { borderColor: "#005BF0", borderWidth: 1 },
  },
};

const selectSx = {
  borderRadius: "4px",
  fontSize: 14,
  fontFamily: FONT,
  "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
};

const truthInputSx = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  py: 1.25,
  "& .MuiOutlinedInput-root": {
    bgcolor: "transparent",
    fontFamily: FONT,
    fontSize: 13,
    lineHeight: 1.5,
    p: 0,
    alignItems: "center",
    "& fieldset": { border: "none" },
    "& textarea": {
      px: 1.5,
      py: 0,
      minHeight: "unset !important",
    },
  },
};

const expectedBoxSx = {
  bgcolor: "#FFFFFF",
  border: "1px solid #E6E6E6",
  borderLeft: "4px solid #14B872",
  borderRadius: "4px",
  ...truthInputSx,
};

const prohibitedBoxSx = {
  bgcolor: "#FFFFFF",
  border: "1px solid #E6E6E6",
  borderLeft: "4px solid #DE350B",
  borderRadius: "4px",
  ...truthInputSx,
};

function FieldLabel({ children, required }) {
  return (
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#344054", mb: 0.75, fontFamily: FONT }}>
      {required && (
        <Typography component="span" sx={{ color: "#DE350B", mr: 0.25 }}>
          *
        </Typography>
      )}
      {children}
    </Typography>
  );
}

function sourceLabel(source) {
  if (source === "Custom") return "Custom";
  if (source === "Modified") return "Modified";
  return "Generated";
}

function sourceBadgeSx(source) {
  if (source === "Custom") return { bgcolor: "#FFEDD5", color: "#C2410C" };
  if (source === "Modified") return { bgcolor: "#E6E6E6", color: "#555555" };
  return { bgcolor: "#F5EEFF", color: "#7F2CE8" };
}

function formatUpdatedAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function promptsFromCase(testCase) {
  if (!Array.isArray(testCase?.input) || !testCase.input.length) return [""];
  return testCase.input.map((item) => {
    if (typeof item === "string") return item;
    if (item && typeof item === "object") return item.text || item.prompt || item.content || "";
    return "";
  });
}

function metricOptionLabel(metric) {
  const code = metric.metricId ? String(metric.metricId).slice(0, 12).toUpperCase() : metric.id.slice(0, 8).toUpperCase();
  return `${metric.metricName} (${code})`;
}

function TraceRow({ label, value }) {
  return (
    <Box sx={{ py: 0.6 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", color: "#707070", fontFamily: FONT }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#383838", fontFamily: FONT, mt: 0.2, wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function EditReviewTestCaseDrawer({
  open,
  testCase,
  metrics = [],
  risks = [],
  onClose,
  onConfirm,
  busy = false,
  isContentGenerator = false,
}) {
  const [name, setName] = useState("");
  const [trustAttribute, setTrustAttribute] = useState("");
  const [testType, setTestType] = useState("nominal");
  const [metricId, setMetricId] = useState("");
  const [expected, setExpected] = useState("");
  const [prohibited, setProhibited] = useState("");
  const [prompts, setPrompts] = useState([""]);
  const [strategyOpen, setStrategyOpen] = useState(false);
  const expectedLabels = expectedBehaviorFieldLabels(isContentGenerator);

  const attributes = useMemo(() => {
    const fromMetrics = metrics.map((m) => m.trustAttribute).filter(Boolean);
    const unique = Array.from(new Set(fromMetrics));
    if (testCase?.trustAttribute && !unique.includes(testCase.trustAttribute)) {
      unique.unshift(testCase.trustAttribute);
    }
    return unique;
  }, [metrics, testCase]);

  const riskById = useMemo(() => new Map(risks.map((r) => [r.id, r])), [risks]);
  const selectedMetric = metrics.find((m) => m.id === metricId) || null;
  const linkedRisk = selectedMetric ? riskById.get(selectedMetric.riskId) : null;

  useEffect(() => {
    if (!open || !testCase) return;
    setName(testCase.name || "");
    setTrustAttribute(testCase.trustAttribute || "");
    setTestType(testCase.testType || "nominal");
    setMetricId(testCase.metricId || "");
    setExpected(unwrapResponseText(testCase.groundTruth?.expected_output || testCase.groundTruth?.expected_behavior || ""));
    setProhibited(testCase.groundTruth?.fail_criteria || "");
    setPrompts(promptsFromCase(testCase));
    setStrategyOpen(false);
  }, [open, testCase]);

  if (!testCase) return null;

  const validPrompts = prompts.map((p) => p.trim()).filter(Boolean);
  const canSave = name.trim() && validPrompts.length > 0 && trustAttribute && metricId;
  const updatedLabel = formatUpdatedAt(testCase.updatedAt || testCase.createdAt);
  const status = testCase.status || "not_reviewed";
  const metricCode = testCase.metricCode || "";
  const riskName = linkedRisk?.name || testCase.riskName || "";
  const metricName = selectedMetric?.metricName || testCase.metricName || "";

  const handleMetricChange = (e) => {
    const nextId = e.target.value;
    const metric = metrics.find((m) => m.id === nextId);
    setMetricId(nextId);
    if (metric?.trustAttribute) setTrustAttribute(metric.trustAttribute);
  };

  const handleConfirm = () => {
    if (!canSave || busy) return;
    onConfirm({
      name: name.trim(),
      trust_attribute: trustAttribute,
      test_type: testType,
      metric_id: metricId,
      expected_output: expected.trim(),
      fail_criteria: prohibited.trim(),
      input: validPrompts,
      status: "verified",
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={busy ? undefined : onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "min(92vw, 520px)", md: 520 },
          maxWidth: "100vw",
          height: { xs: "100%", sm: "100%" },
          boxShadow: "-8px 0 32px rgba(16,24,40,0.14)",
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(52,64,84,0.45)" } } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 1.75, borderBottom: "1px solid #EAECF0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 16, color: "#555555" }}>
                {testCase.name || "—"}
              </Typography>
              <Chip
                label={sourceLabel(testCase.source)}
                size="small"
                sx={{
                  height: 24,
                  fontWeight: 700,
                  fontSize: 12,
                  borderRadius: testCase.source === "Generated" ? "12px" : "999px",
                  ...sourceBadgeSx(testCase.source),
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
              <Chip
                label={STATUS_LABEL[status] || status}
                size="small"
                sx={{
                  height: 24,
                  fontWeight: 600,
                  fontSize: 12,
                  borderRadius: "999px",
                  ...(STATUS_SX[status] || {}),
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
              <Chip
                label={
                  TEST_TYPE_LABELS[String(testType || "").toLowerCase().replace(/[\s-]+/g, "_")] ||
                  TEST_TYPE_LABELS[testType] ||
                  "Functional"
                }
                size="small"
                variant="filled"
                sx={{
                  height: 24,
                  fontWeight: 600,
                  fontSize: 12,
                  fontFamily: FONT,
                  borderRadius: "999px",
                  bgcolor: "#F5F5F5",
                  color: "#555555",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              {updatedLabel ? (
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: 12,
                    color: "#707070",
                    whiteSpace: "nowrap",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  Last updated: {updatedLabel}
                </Typography>
              ) : null}
              <IconButton size="small" onClick={onClose} disabled={busy} aria-label="Close" sx={{ color: "#555555" }}>
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 16, color: "#555555", mt: 2, wordBreak: "break-word" }}>
            {name.trim() || testCase.name || "Untitled test case"}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", px: { xs: 2, sm: 3 }, py: 2 }}>
          <Stack spacing={2.25}>
            <Box>
              <FieldLabel required>Test Case Title</FieldLabel>
              <TextField fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)} sx={fieldSx} />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <Box>
                <FieldLabel required>Trust Attribute</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select IconComponent={DropdownIcon}
                    value={trustAttribute}
                    onChange={(e) => setTrustAttribute(e.target.value)}
                    displayEmpty
                    sx={selectSx}
                  >
                    <MenuItem value="" disabled>
                      Select attribute
                    </MenuItem>
                    {attributes.map((a) => (
                      <MenuItem key={a} value={a}>
                        {a}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <FieldLabel required>Test Type</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select IconComponent={DropdownIcon}
                    value={testType}
                    onChange={(e) => setTestType(e.target.value)}
                    sx={selectSx}
                  >
                    {TEST_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {TEST_TYPE_LABELS[t]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box>
              <FieldLabel required>Linked Metric</FieldLabel>
              <FormControl fullWidth size="small">
                <Select IconComponent={DropdownIcon}
                  value={metricId}
                  onChange={handleMetricChange}
                  displayEmpty
                  sx={selectSx}
                >
                  <MenuItem value="" disabled>
                    Select metric
                  </MenuItem>
                  {metrics.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {metricOptionLabel(m)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ border: "1px solid #E6E6E6", borderRadius: "8px", px: 2, py: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#344054", mb: 1, fontFamily: FONT }}>
                Traceability
              </Typography>
              <TraceRow label="RISK" value={riskName || "—"} />
              <TraceRow label="METRIC" value={metricName ? `${metricName}${metricCode ? ` : ${metricCode}` : ""}` : "—"} />
              <Box sx={{ py: 0.6 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", color: "#707070", fontFamily: FONT }}>
                  TEST STRATEGY
                </Typography>
                <Box
                  onClick={() => setStrategyOpen((v) => !v)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mt: 0.75,
                    px: 1.25,
                    py: 1,
                    bgcolor: "#E8F1FF",
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: "#005BF0",
                    minWidth: 0,
                  }}
                >
                  {strategyOpen ? <ExpandMoreIcon sx={{ fontSize: 18, flexShrink: 0 }} /> : <ChevronRightIcon sx={{ fontSize: 18, flexShrink: 0 }} />}
                  <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: FONT, color: "#005BF0", wordBreak: "break-word" }}>
                    {trustAttribute ? `${trustAttribute} validation` : "Test strategy"}
                    {testCase.strategySectionId ? ` | ${String(testCase.strategySectionId).toUpperCase()}` : ""}
                  </Typography>
                </Box>
                <Collapse in={strategyOpen}>
                  <Typography sx={{ fontSize: 12, color: "#667085", fontFamily: FONT, mt: 0.75, pl: 1 }}>
                    Traceability is created automatically from the linked metric.
                  </Typography>
                </Collapse>
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: "#F5F5F5",
                border: "1px solid #E6E6E6",
                borderRadius: "8px",
                p: 1.5,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#344054", mb: 1.25, fontFamily: FONT }}>
                Ground Truth
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                <Box sx={expectedBoxSx}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: "0.04em",
                      color: "#14B872",
                      px: 1.5,
                      fontFamily: FONT,
                    }}
                  >
                    {expectedLabels.banner}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={6}
                    value={expected}
                    onChange={(e) => setExpected(e.target.value)}
                    placeholder="To be defined by a reviewer."
                  />
                </Box>
                <Box sx={prohibitedBoxSx}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: "0.04em",
                      color: "#DE350B",
                      px: 1.5,
                      fontFamily: FONT,
                    }}
                  >
                    PROHIBITED
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={6}
                    value={prohibited}
                    onChange={(e) => setProhibited(e.target.value)}
                    placeholder="To be defined by a reviewer."
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: { xs: 1.25, sm: 2 },
                  pt: 1.5,
                  mt: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#707070", fontFamily: FONT, mb: 0.5 }}>
                    SME Status
                  </Typography>
                  <Chip
                    label={STATUS_LABEL[status] || status}
                    size="small"
                    sx={{
                      height: 22,
                      fontWeight: 600,
                      fontSize: 12,
                      borderRadius: "999px",
                      ...(STATUS_SX[status] || {}),
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                </Box>
                <Box sx={{ textAlign: { xs: "left", sm: "right" }, minWidth: 0, flex: { xs: "1 1 100%", sm: "1 1 auto" } }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#707070", fontFamily: FONT, mb: 0.5 }}>
                    Scored against
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#383838", fontFamily: FONT, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {metricName ? `${metricName}${metricCode ? ` : ${metricCode}` : ""}` : "—"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <FieldLabel>Test Input / Prompt</FieldLabel>
              <Typography sx={{ fontSize: 12, color: "#98A2B3", mb: 1, fontFamily: FONT, lineHeight: 1.45 }}>
                {expectedLabels.helper}
              </Typography>
              <Stack spacing={1.25}>
                {prompts.map((p, idx) => (
                  <Box key={idx}>
                    <FieldLabel>Prompt ({idx + 1})</FieldLabel>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        value={p}
                        onChange={(e) => setPrompts((prev) => prev.map((item, i) => (i === idx ? e.target.value : item)))}
                        sx={fieldSx}
                      />
                      {prompts.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => setPrompts((prev) => prev.filter((_, i) => i !== idx))}
                          sx={{ color: "#D6441F", mt: 0.5 }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => setPrompts((prev) => [...prev, ""])}
                sx={{ textTransform: "none", fontWeight: 700, color: "#005BF0", mt: 0.75, px: 0 }}
              >
                Add New Prompt
              </Button>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderTop: "1px solid #EAECF0",
            bgcolor: "#F5F5F5",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: { xs: 1.25, sm: 2 },
            position: "sticky",
            bottom: 0,
            zIndex: 1,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#667085", fontFamily: FONT, flex: 1, pr: { sm: 1 } }}>
            Confirming locks the ground truth and marks this case as validated.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0, justifyContent: { xs: "stretch", sm: "flex-end" } }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={busy}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#344054",
                borderColor: "#D0D5DD",
                bgcolor: "#FFFFFF",
                borderRadius: "4px",
                fontFamily: FONT,
                flex: { xs: 1, sm: "none" },
                "&:hover": { bgcolor: "#F9FAFB", borderColor: "#D0D5DD" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={busy || !canSave}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#005BF0",
                borderRadius: "4px",
                boxShadow: "none",
                fontFamily: FONT,
                flex: { xs: 1, sm: "none" },
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "#1251c0", boxShadow: "none" },
              }}
            >
              Confirm ground truth
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
